import {
  type ColumnVisibilityState,
  columnFilteringFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createExpandedRowModel,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_equals,
  filterFn_includesString,
  metaHelper,
  type RowData,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  type CellContext as TanstackCellContext,
  type Column as TanstackColumn,
  type ColumnDef as TanstackColumnDef,
  type Header as TanstackHeader,
  type Row as TanstackRow,
  tableFeatures,
} from '@tanstack/react-table';

/** Shared Hub/UI table feature set — register once so ColumnDef stays a one-arg type. */
export const forestTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  expandedRowModel: createExpandedRowModel(),
  filterFns: {
    equals: filterFn_equals,
    includesString: filterFn_includesString,
  },
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
  columnMeta: metaHelper<{ align?: 'left' | 'center' | 'right' }>(),
  tableMeta: metaHelper<{
    updateData?: (rowId: string, columnId: string, value: unknown) => void;
    getOriginalValue?: (rowId: string, columnId: string) => unknown;
  }>(),
});

export type ForestTableFeatures = typeof forestTableFeatures;

export type ColumnDef<TData extends RowData, TValue = unknown> = TanstackColumnDef<ForestTableFeatures, TData, TValue>;
export type Column<TData extends RowData, TValue = unknown> = TanstackColumn<ForestTableFeatures, TData, TValue>;
export type Row<TData extends RowData> = TanstackRow<ForestTableFeatures, TData>;
export type CellContext<TData extends RowData, TValue = unknown> = TanstackCellContext<ForestTableFeatures, TData, TValue>;
export type Header<TData extends RowData, TValue = unknown> = TanstackHeader<ForestTableFeatures, TData, TValue>;

export type { RowData };
export type VisibilityState = ColumnVisibilityState;

export type {
  ColumnFiltersState,
  ColumnSizingState,
  ExpandedState,
  PaginationState,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table';
export {
  constructSortFn,
  flexRender,
  useTable,
} from '@tanstack/react-table';
